/**
 * "Remind me about this later."
 *
 * A deliberately small thing, and honest about being small. The platform has no
 * reminder of its own to write to — there is a notifications feed, but that is
 * the platform telling the operator something, not the operator leaving a note
 * for themselves. So these live in this browser, under this operator's key.
 *
 * That limit is stated on the screen rather than buried here, because the
 * failure mode is specific and bad: someone leaves themselves a reminder about
 * a P1, sits at a different terminal the next morning, and finds nothing. A
 * reminder that might silently not be there is worse than a note on paper, and
 * an operator who knows the rule can decide which to use.
 *
 * When the platform grows somewhere to put these, `load` and `save` are the
 * only two functions that change.
 */

export interface Reminder {
  id: string;
  text: string;
  /** Epoch ms. */
  dueAt: number;
  createdAt: number;
  /** Optional record this is about, so the reminder can link back. */
  about?: { label: string; href?: string };
  doneAt?: number;
}

const PREFIX = "skylogics.assistant.reminders";
const keyFor = (who: string) => `${PREFIX}.${who}`;

export function loadReminders(who: string): Reminder[] {
  try {
    const raw = localStorage.getItem(keyFor(who));
    if (!raw) return [];
    const list = JSON.parse(raw) as Reminder[];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export function saveReminders(who: string, list: readonly Reminder[]): void {
  try {
    localStorage.setItem(keyFor(who), JSON.stringify(list));
  } catch {
    /* the caller keeps working from memory; it simply will not survive a reload */
  }
}

let seq = 0;
export function newReminder(text: string, dueAt: number, about?: Reminder["about"]): Reminder {
  return { id: `r${Date.now().toString(36)}${++seq}`, text: text.trim(), dueAt, createdAt: Date.now(), about };
}

export const isDue = (r: Reminder, now: number = Date.now()): boolean => !r.doneAt && r.dueAt <= now;

export const isPending = (r: Reminder): boolean => !r.doneAt;

/** Due first, then soonest. Done ones fall to the bottom and stay readable. */
export function ordered(list: readonly Reminder[], now: number = Date.now()): Reminder[] {
  return [...list].sort((a, b) => {
    if (Boolean(a.doneAt) !== Boolean(b.doneAt)) return a.doneAt ? 1 : -1;
    const ad = isDue(a, now) ? 0 : 1;
    const bd = isDue(b, now) ? 0 : 1;
    return ad - bd || a.dueAt - b.dueAt;
  });
}

/* ── the shorthand an operator will actually type ──────────────────────── */

export interface ParsedWhen {
  dueAt: number;
  /** How it was read back, so the operator can see it was understood. */
  said: string;
}

/**
 * Understands a few plain phrasings and nothing more.
 *
 * The restraint is the point. A parser that guesses at "after the sweep" will
 * eventually guess wrong about a P1, and the operator has no way to tell a
 * confident wrong time from a right one. Anything not recognised here comes
 * back null, and the caller asks for a time instead of inventing one.
 */
export function parseWhen(input: string, now: number = Date.now()): ParsedWhen | null {
  const t = input.toLowerCase().trim();
  const base = new Date(now);

  const rel = /^(?:in\s+)?(\d{1,3})\s*(m|min|mins|minute|minutes|h|hr|hrs|hour|hours|d|day|days)\b/.exec(t);
  if (rel) {
    const n = Number(rel[1]);
    const unit = rel[2][0];
    const ms = unit === "m" ? n * 60_000 : unit === "h" ? n * 3_600_000 : n * 86_400_000;
    const word = unit === "m" ? "minute" : unit === "h" ? "hour" : "day";
    return { dueAt: now + ms, said: `in ${n} ${word}${n === 1 ? "" : "s"}` };
  }

  const at = /^(?:at\s+)?([01]?\d|2[0-3]):([0-5]\d)$/.exec(t);
  if (at) {
    const d = new Date(base);
    d.setHours(Number(at[1]), Number(at[2]), 0, 0);
    if (d.getTime() <= now) d.setDate(d.getDate() + 1);
    const when = d.getTime() - now > 43_200_000 ? "tomorrow" : "today";
    return { dueAt: d.getTime(), said: `${when} at ${at[1].padStart(2, "0")}:${at[2]}` };
  }

  if (/^tomorrow$/.test(t)) {
    const d = new Date(base);
    d.setDate(d.getDate() + 1);
    d.setHours(8, 0, 0, 0);
    return { dueAt: d.getTime(), said: "tomorrow at 08:00" };
  }

  if (/^(later|this afternoon)$/.test(t)) {
    const d = new Date(base);
    d.setHours(Math.max(14, d.getHours() + 2), 0, 0, 0);
    if (d.getTime() <= now) d.setTime(now + 7_200_000);
    return { dueAt: d.getTime(), said: "later today" };
  }

  if (/^(end of (the )?(day|shift))$/.test(t)) {
    const d = new Date(base);
    d.setHours(17, 0, 0, 0);
    if (d.getTime() <= now) d.setDate(d.getDate() + 1);
    return { dueAt: d.getTime(), said: "at the end of the day" };
  }

  return null;
}

/** "due now", "in 25 minutes", "overdue by 2 hours" — for the list. */
export function dueWords(r: Reminder, now: number = Date.now()): string {
  const ms = r.dueAt - now;
  const abs = Math.abs(ms);
  const mins = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / 86_400_000);
  const size =
    mins < 60
      ? `${mins} minute${mins === 1 ? "" : "s"}`
      : hours < 48
        ? `${hours} hour${hours === 1 ? "" : "s"}`
        : `${days} days`;
  if (Math.abs(ms) < 60_000) return "due now";
  return ms > 0 ? `in ${size}` : `overdue by ${size}`;
}
